import { useWebSocketContext } from 'context/WebSocketConnectionContext';
import { useCallback, useEffect, useState } from 'react';
import { listEmailThreads } from 'services/mailbox/mailboxService';

const useEmails = (searchKeyword, setReadEmails = () => {}, isSent = false, filters, limit = 10) => {
  const [data, setData] = useState([]);
  const [emails, setEmails] = useState([]);
  const [unassignedEmails, setUnassignedEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [paginationToken, setPaginationToken] = useState(null);

  const { caseEmails } = useWebSocketContext();

  const [isForward, setIsForward] = useState(true);

  const next_Token = data?.next_token;
  const transition_Token = data?.transition_token;

  const hasNextPage = next_Token === null && isForward;
  const hasPreviousPage = transition_Token === null || (transition_Token && next_Token === null && !isForward);

  const fetchNext = () => {
    setIsForward(true);
    setPaginationLoading(true);
    const token = !isForward ? transition_Token : next_Token;

    listEmailThreads(token, null, null, null, false, searchKeyword, 'forward', false, filters?.mappingStatuses, filters?.userFilter)
      .then((res) => {
        setData(res?.data);
        const nextToken = res?.data?.next_token;
        setPaginationToken(res?.data?.next_token);
        setHasNext(!!nextToken);
        setUnassignedEmails(res?.data?.email_threads);
      })
      .catch((err) => {
        setError(err);
        console.error(err);
      })
      .finally(() => setPaginationLoading(false));
  };

  const fetchPrev = () => {
    setIsForward(false);
    setPaginationLoading(true);
    const token = isForward ? transition_Token : next_Token;
    listEmailThreads(token, null, null, null, false, searchKeyword, 'reverse', false, filters?.mappingStatuses, filters?.userFilter)
      .then((res) => {
        setData(res?.data);
        const emailThreads = res?.data?.email_threads;
        setUnassignedEmails(emailThreads);
      })
      .catch((err) => {
        setError(err);
        console.error(err);
      })
      .finally(() => setPaginationLoading(false));
  };

  const fetchEmailThreads = useCallback(async () => {
    setLoading(true);
    setError(null);
    listEmailThreads(
      null,
      filters.caseId,
      filters.mail_date_lower,
      filters.mail_date_upper,
      isSent,
      searchKeyword,
      null,
      false,
      filters.mappingStatuses,
      filters.userFilter,
      limit
    )
      .then((res) => {
        // const emailThreads = res?.data?.email_threads;
        const emailThreads = res?.data?.email_threads.map((mail, idx) => ({
          ...mail,
          index: idx,
        }));
        if (!filters.caseId) {
          setUnassignedEmails(emailThreads);
        } else {
          setEmails(emailThreads);
        }
        const nextToken = res?.data?.next_token;

        setPaginationToken(nextToken);
        setHasNext(!!nextToken);
      })
      .catch((err) => {
        setError(err);
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [isSent, searchKeyword, filters]);

  const fetchNextEmailThreads = async () => {
    if (!paginationToken || paginationLoading || loading) return;
    setPaginationLoading(true);
    setHasNext(false);
    setError(null);
    listEmailThreads(
      paginationToken,
      filters.caseId,
      filters.mail_date_lower,
      filters.mail_date_upper,
      isSent,
      searchKeyword,
      null,
      false,
      filters.mappingStatuses,
      filters.userFilter,
      limit
    )
      .then((res) => {
        const emailThreads = res?.data?.email_threads;
        if (!filters.caseId) {
          setUnassignedEmails((prevEmails) => [
            ...prevEmails,
            ...emailThreads.map((mail, idx) => ({
              ...mail,
              index: prevEmails.length + idx,
            })),
          ]);
          // setUnassignedEmails((prevEmails) => [...prevEmails, ...emailThreads]);
        } else {
          setEmails((prevEmails) => [
            ...prevEmails,
            ...emailThreads.map((mail, idx) => ({
              ...mail,
              index: prevEmails.length + idx,
            })),
          ]);
          // setEmails((prevEmails) => [...prevEmails, ...emailThreads]);
        }
        const nextToken = res?.data?.next_token;
        setPaginationToken(nextToken);
        setHasNext(!!nextToken);
      })
      .catch((err) => {
        setError(err);
        console.error(err);
      })
      .finally(() => setPaginationLoading(false));
  };

  const handleLoadMore = () => {
    // Prevent duplicate calls - check if already loading or no more data
    if (paginationLoading || loading) return;
    fetchNextEmailThreads();
  };

  const refreshEmails = useCallback(() => {
    fetchEmailThreads();
  }, [fetchEmailThreads]);

  useEffect(() => {
    if (!!caseEmails?.body.case_id) {
      // if incoming email from ws has case id
      setEmails((prevEmails) => {
        const index = prevEmails.findIndex((email) => email.pk === caseEmails?.body.pk);
        if (index !== -1) {
          return prevEmails.map((email, i) => {
            setReadEmails((prev) => ({
              ...prev,
              [caseEmails?.body.pk]: false, // Ensure it remains unread
            }));
            return i === index ? { ...email, is_unread: true, subject: caseEmails?.body.subject } : email;
          });
        } else {
          const newEmail = { ...caseEmails?.body, is_unread: true };
          return [newEmail, ...prevEmails];
        }
      });
    } else if (hasPreviousPage) {
      // if reconciliation page is paginated, then don't need to add new emails from ws
      setUnassignedEmails((prevEmails) => {
        const index = prevEmails.findIndex((email) => email.pk === caseEmails?.body.pk);
        if (index !== -1) {
          return prevEmails.map((email, i) => (i === index ? { ...email, is_unread: true, subject: caseEmails?.body.subject } : email));
        }
        return [{ ...caseEmails?.body, is_unread: true }, ...prevEmails];
      });
    }
  }, [caseEmails]);

  useEffect(() => {
    if (filters.caseId || filters.mail_date_lower || filters.mail_date_upper || filters.mappingStatuses || filters.userFilter) {
      const timeout = setTimeout(() => {
        fetchEmailThreads(); // for getting emails with caseId and date filter
      }, 300); // wait 300ms after last change

      return () => clearTimeout(timeout);
    } else fetchNext(); // use this for getting unassigned emails
  }, [isSent, searchKeyword, filters]);

  return {
    emails,
    unassignedEmails,
    setEmails,
    handleLoadMore,
    refreshEmails,
    loading,
    error,
    hasNext,
    hasNextPage,
    hasPreviousPage,
    paginationLoading,
    fetchNext,
    fetchPrev,
  };
};

export default useEmails;
